import { SearchBackground } from '@/app/icons/SearchBackground'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import { MdTune } from 'react-icons/md'
import { SearchBox, SearchBoxProps } from 'react-instantsearch'

type CustomSearchBoxProps = SearchBoxProps & {
  onFilterClick?: () => void
  hideFilters?: boolean
  noIcon?: boolean
}

export function CustomSearchBox({
  onFilterClick,
  hideFilters = false,
  noIcon = false,
  ...searchBoxProps
}: CustomSearchBoxProps) {
  const t = useTranslations('header')

  return (
    <SearchBox
      {...searchBoxProps}
      placeholder={t('text_search_placeholder')}
      classNames={{
        root: cn('w-full'),
        form: cn(
          'relative flex items-center',
          'bg-white',
          'rounded-[10px] md:rounded-xl',
          'border border-solid border-lines',
          'focus-within:outline focus-within:outline-2 focus-within:outline-mydra-black'
        ),
        input: cn(
          'flex-1',
          'w-full',
          'max-h-[50px]',
          'py-[6px] pl-2 pr-4 md:py-[9px] md:pl-6 ',
          'text-sm md:text-base',
          'text-mydra-black',
          'bg-transparent',
          'border-none',
          'outline-none',
          'focus:ring-0',
          'placeholder:text-gray-light'
        ),
        submit: cn(
          'flex items-center justify-center gap-1 md:gap-2 my-1 mx-2',
          'order-3',
          'bg-transparent border-none',
          'cursor-pointer',
          noIcon && 'hidden'
        ),
        submitIcon: cn('flex items-center justify-center'),
        reset: cn(
          'absolute right-14',
          'bg-transparent border-none',
          'cursor-pointer',
          'hidden',
          '[.ais-SearchBox-form:not(:has(.ais-SearchBox-input:placeholder-shown))_&]:flex'
        ),
        resetIcon: cn('text-gray-light hover:text-mydra-black', 'text-xl'),
        loadingIndicator: cn('absolute right-2'),
        loadingIcon: cn('text-mydra-purple', 'text-xl'),
        ...searchBoxProps.classNames,
      }}
      submitIconComponent={({ classNames }) =>
        !noIcon ? (
          <div
            className={cn(
              classNames.submitIcon,
              'flex items-center gap-1 md:gap-2'
            )}
          >
            {!hideFilters && (
              <button
                type="button"
                onClick={onFilterClick}
                className="order-1 flex items-center gap-1"
              >
                <MdTune className="size-6 text-mydra-black md:size-5" />
                <span className="hidden text-sm text-mydra-black md:inline">
                  {t('filters')}
                </span>
              </button>
            )}
            <button type="submit" className="order-2">
              <SearchBackground width={33} height={33} className="md:hidden" />
              <SearchBackground
                width={40}
                height={40}
                className="hidden md:block"
              />
            </button>
          </div>
        ) : null
      }
      resetIconComponent={({ classNames }) => (
        <svg
          className={classNames.resetIcon}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 5L5 15M5 5L15 15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      loadingIconComponent={({ classNames }) => (
        <svg
          className={cn(classNames.loadingIcon, 'animate-spin')}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            className="opacity-25"
            cx="10"
            cy="10"
            r="8"
            stroke="currentColor"
            strokeWidth="3"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M10 2a8 8 0 018 8h-3a5 5 0 00-5-5V2z"
          />
        </svg>
      )}
    />
  )
}
